import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchChallanList,
  createChallan,
  fetchChallanById,
  updateChallan,
  fetchChallanCreateData,
  fetchAccessoryReturnData,
  createAccessoryReturn,
  fetchAccessoryReturnEditData,
  updateAccessoryReturn,
} from "./thunk";

interface ChallanState {
  loading: boolean;
  error: string | null;
  challanList: any[];
  challanDetail: any | null;
  accessoryReturnData: any | null;
  accessoryReturnEditData: any | null;
  operationSuccess: boolean;
}

const initialState: ChallanState = {
  loading: false,
  error: null,
  challanList: [],
  challanDetail: null,
  accessoryReturnData: null,
  accessoryReturnEditData: null,
  operationSuccess: false,
};

const challanSlice = createSlice({
  name: "Challan",
  initialState,
  reducers: {
    clearChallanState: (state) => {
      state.loading = false;
      state.error = null;
      state.operationSuccess = false;
      state.challanDetail = null;
      state.accessoryReturnData = null;
      state.accessoryReturnEditData = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: ChallanState) => {
      state.loading = true;
      state.error = null;
      state.operationSuccess = false;
    };

    const handleRejected = (state: ChallanState, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload || "Something went wrong";
      state.operationSuccess = false;
    };

    // Fetch Challan List
    builder
      .addCase(fetchChallanList.pending, handlePending)
      .addCase(fetchChallanList.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.challanList = action.payload;
      })
      .addCase(fetchChallanList.rejected, handleRejected);

    // Fetch Challan By ID (for editing)
    builder
      .addCase(fetchChallanById.pending, handlePending)
      .addCase(fetchChallanById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.challanDetail = action.payload;
      })
      .addCase(fetchChallanById.rejected, handleRejected);

    // Fetch Create Data (for new challan)
    builder
      .addCase(fetchChallanCreateData.pending, handlePending)
      .addCase(fetchChallanCreateData.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.challanDetail = action.payload;
      })
      .addCase(fetchChallanCreateData.rejected, handleRejected);

    // Create Challan
    builder
      .addCase(createChallan.pending, handlePending)
      .addCase(createChallan.fulfilled, (state) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(createChallan.rejected, handleRejected);

    // Update Challan
    builder
      .addCase(updateChallan.pending, handlePending)
      .addCase(updateChallan.fulfilled, (state) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(updateChallan.rejected, handleRejected);

    builder
      .addCase(fetchAccessoryReturnData.pending, handlePending)
      .addCase(fetchAccessoryReturnData.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.accessoryReturnData = action.payload;
      })
      .addCase(fetchAccessoryReturnData.rejected, handleRejected);

    builder
      .addCase(createAccessoryReturn.pending, handlePending)
      .addCase(createAccessoryReturn.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
        if (action.payload) {
          state.accessoryReturnData = {
            ...(state.accessoryReturnData || {}),
            returned_accessories: action.payload.returned_accessories,
            accessory_return_summary: action.payload.accessory_return_summary,
          };
        }
      })
      .addCase(createAccessoryReturn.rejected, handleRejected);

    builder
      .addCase(fetchAccessoryReturnEditData.pending, handlePending)
      .addCase(fetchAccessoryReturnEditData.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.accessoryReturnEditData = action.payload;
      })
      .addCase(fetchAccessoryReturnEditData.rejected, handleRejected);

    builder
      .addCase(updateAccessoryReturn.pending, handlePending)
      .addCase(updateAccessoryReturn.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
        state.accessoryReturnEditData = null;
      })
      .addCase(updateAccessoryReturn.rejected, handleRejected);
  },
});

export const { clearChallanState } = challanSlice.actions;
export default challanSlice.reducer;
